import {useState} from 'react';

import {Dialog} from 'shells/dialog/dialog';
import {Modal} from 'shells/modal/modal';
import {DripSequenceRow} from 'shells/row/drip-sequence-row/drip-sequence-row';

import type {DripProgressionFragment} from 'nutshell-graphql-types';

import {MessageBodyPreviewModal} from '../../../email-marketing/drip-sequences/recipient-modal/message-body-preview-modal';

import type {ConfirmState} from './types';

interface Props {
    contactId: string;
    contactName: string;
    dripSequence: DripProgressionFragment | null | undefined;
    onCancelSequence: (id: string) => void;
    onClose: () => void;
}

export function DripSequenceDetailsModal(props: Props) {
    const [confirmingCancelId, setConfirmingCancelId] = useState<ConfirmState | null | undefined>(
        null
    );
    const [dripSequenceToPreview, setDripSequenceToPreview] = useState<
        | {
              emailTemplateId: string;
              entityId: string;
          }
        | null
        | undefined
    >(null);

    if (!props.dripSequence) {
        return null;
    }

    const stopCondition = props.dripSequence.campaign.isStopOnLeadClose
        ? 'Stop sending when the lead closes'
        : props.dripSequence.campaign.goal &&
          props.dripSequence.campaign.goal.humanReadableSubtitle;

    return (
        <>
            {!dripSequenceToPreview && (
                <Modal isOpen={true} onClose={props.onClose} headerText='Drip sequence details'>
                    <DripSequenceRow
                        key={props.dripSequence.id}
                        id={props.dripSequence.id}
                        title={props.dripSequence.campaign.name}
                        htmlUrl={props.dripSequence.campaign.htmlUrlPath}
                        messages={props.dripSequence.editions}
                        startTrigger={
                            props.dripSequence.campaign.startTrigger &&
                            props.dripSequence.campaign.startTrigger.humanReadableSubtitle
                        }
                        stopCondition={stopCondition}
                        onCancelButtonClick={(dripId: string, buttonRef?: HTMLElement | null) => {
                            setConfirmingCancelId({
                                buttonRef,
                                sequenceId: dripId,
                            });
                        }}
                        onViewMessage={(emailTemplateId: string) => {
                            setDripSequenceToPreview({
                                emailTemplateId,
                                entityId: props.contactId,
                            });
                        }}
                        forceShowToolbar={Boolean(
                            confirmingCancelId && confirmingCancelId.buttonRef
                        )}
                        recipientName={props.contactName}
                    />
                </Modal>
            )}
            {dripSequenceToPreview && (
                <MessageBodyPreviewModal
                    emailTemplateId={dripSequenceToPreview.emailTemplateId}
                    entityId={dripSequenceToPreview.entityId}
                    onClose={() => {
                        setDripSequenceToPreview(null);
                    }}
                />
            )}
            {confirmingCancelId && confirmingCancelId.buttonRef && (
                <Dialog
                    anchor={confirmingCancelId.buttonRef}
                    question='Are you sure you want to cancel this drip sequence?'
                    onPrimary={() => {
                        props.onCancelSequence(confirmingCancelId.sequenceId);
                        props.onClose();
                    }}
                    onSecondary={() => {
                        setConfirmingCancelId(null);
                    }}
                    primaryText='Yes, cancel'
                    secondaryText="No, don't cancel"
                    primaryVariant='danger'
                    secondaryVariant='secondary'
                    customStyles={{width: '240px'}}
                />
            )}
        </>
    );
}
